import {Button, Menu, MenuItem, Popover, Tooltip} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {ICellRendererParams} from "@ag-grid-community/core";
import {AgStop} from "../../common/types";
import {RowData} from "../types/types";
import {isCompleted} from "../../../utils/stops/StopUtils";

type StopActionMenuParams = ICellRendererParams<AgStop> & {
  workingRowDataRef: React.MutableRefObject<RowData[]>;
  onMoveJobClick: (stop: AgStop, parentDriverId: number, parentRouteId: string) => void;
  onDeleteJob: (jobId: number, driverRowIndex: number) => void;
  parentDriverId: number;
  parentRouteId: string;
};

const StopActionMenuCellRenderer = (params: StopActionMenuParams) => {
  const {data, workingRowDataRef, onMoveJobClick, onDeleteJob, parentDriverId, parentRouteId} = params;

  if (!data) return null;

  const jobId = data.job?.jobId;
  if (!jobId) return null;

  // DP-3175: Completed stops are frozen — disable all actions.
  const isCompletedStop = isCompleted(data);

  // DP-3175 B08: Partially completed jobs (any sibling stop completed) must stay
  // on their manifest — disable move and delete.
  const parentRoute = workingRowDataRef.current.find((row) => row.routeId === parentRouteId);
  const isPartiallyCompleted =
    !isCompletedStop && (parentRoute?.assignedStops.some((s) => s.job?.jobId === jobId && isCompleted(s)) ?? false);

  const isDisabled = isCompletedStop || isPartiallyCompleted;

  const noOtherRoutes = workingRowDataRef.current.filter((row) => row.routeId !== parentRouteId).length === 0;

  return (
    <Popover
      content={
        <Menu>
          <MenuItem
            text="Move Job to Another Route"
            icon={<FontAwesomeIcon icon={solid("arrow-right-arrow-left")} />}
            disabled={noOtherRoutes}
            onClick={() => onMoveJobClick(data, parentDriverId, parentRouteId)}
          />
          <MenuItem
            text="Delete Job"
            icon={<FontAwesomeIcon icon={solid("trash")} />}
            onClick={() => {
              const current = workingRowDataRef.current;
              const driverIndex = current.findIndex((row) => row.routeId === parentRouteId);
              if (driverIndex !== -1) onDeleteJob(jobId, driverIndex);
            }}
          />
        </Menu>
      }
      placement="bottom-start"
      minimal
      disabled={isDisabled}
    >
      <Tooltip
        content="Cannot modify completed or partly completed job's stops"
        disabled={!isDisabled}
        placement="right"
      >
        <Button
          variant="minimal"
          size="small"
          icon={
            <FontAwesomeIcon icon={solid("ellipsis-vertical")} size="lg" color={isDisabled ? "#D1D5DB" : "#6B7280"} />
          }
          style={{minHeight: 20, minWidth: 20, padding: 0}}
          disabled={isDisabled}
        />
      </Tooltip>
    </Popover>
  );
};

export default StopActionMenuCellRenderer;
